import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled, { CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { LineupHeader } from '@sorare/core/src/components/lineup/LineupHeader';
import { LineupLayout } from '@sorare/core/src/components/lobby/LineupLayout';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { range } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { LeagueTeams } from '@sorare/gameplay/src/components/league/LeagueTeams';
import { AppearanceRuleIcon } from '@sorare/gameplay/src/components/lineup/AppearanceRuleIcon';
import { PlayerCard } from '@sorare/gameplay/src/components/lineup/PlayerCard';
import { SimpleCardPlaceholder } from '@sorare/gameplay/src/components/lineup/SimpleCardPlaceholder';
import { Badge } from '@sorare/gameplay/src/components/managerTeam/Badge';
import useComposeTeamAutosave from '@sorare/gameplay/src/hooks/compose-team/useComposeTeamAutosave';
import { useComposeFlowFF } from '@sorare/gameplay/src/hooks/compose/useComposeFlowFF';
import { getComposeTeamRoute } from '@sorare/gameplay/src/lib/so5';

import {
  ForYouTeamsQuery,
  ForYouTeamsQueryVariables,
} from './__generated__/index.graphql';
import header from './assets/header.svg';

const StyledLinkBox = styled(LinkBox)`
  background: var(--c-nd-50-opaque);
  background-image: var(--bg);
  background-size: cover;
  background-position: center;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
`;

const CorderBadgeWrapper = styled.div`
  position: absolute;
  right: calc(-1 * var(--double-unit) - 1px);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
`;
const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

const Header = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
`;
const Placeholder = styled.div`
  filter: drop-shadow(0px 0px 20px rgba(61, 220, 132, 0.8));
  border-radius: var(--half-unit);
`;
const Footer = styled.div`
  height: calc(6 * var(--unit));
  background: var(--c-nd-50);
  border-radius: 0 0 var(--double-unit) var(--double-unit);
`;
const StyledButton = styled(ButtonBase)`
  height: 100%;
  width: 100%;
  padding: var(--unit);
  display: flex;
  justify-content: center;
  align-items: center;
  &&:hover {
    background-color: var(--c-nd-100);
  }
`;

const FOR_YOU_TEAMS_QUERY = gql`
  query ForYouTeamsQuery($slug: String!) {
    so5 {
      so5Fixture(slug: $slug, sport: FOOTBALL) {
        slug
        myMissedLineups {
          slug
          name
          appearances {
            id
            index
            position
            ...PlayerCard_so5Appearance
          }
          so5Leaderboard {
            slug
            displayName: displayName(short: true)
            stadiumUrl
            so5LineupsCount
            so5League {
              slug
            }
            rules {
              id
              appearances {
                name
                ...AppearanceRuleIcon_appearanceRule
              }
            }
            ...ManagerTeamBadge_so5Leaderboard
          }
        }
      }
    }
  }
  ${PlayerCard.fragments.so5Appearance}
  ${AppearanceRuleIcon.fragments.appearanceRule}
  ${Badge.fragments.so5Leaderboard}
` as TypedDocumentNode<ForYouTeamsQuery, ForYouTeamsQueryVariables>;

const MissedLineup = ({
  missedLineup,
}: {
  missedLineup: NonNullable<
    ForYouTeamsQuery['so5']['so5Fixture']
  >['myMissedLineups'][number];
}) => {
  const useComposeFlow20 = useComposeFlowFF();
  const { currentUser } = useCurrentUserContext();
  const navigate = useNavigate();

  const composeSessionId = useMemo(
    () =>
      `${currentUser?.slug}-${missedLineup.so5Leaderboard?.slug}-${new Date().getTime()}`,
    [currentUser?.slug, missedLineup.so5Leaderboard?.slug]
  );

  const { addItemsToAutoSave } = useComposeTeamAutosave(
    missedLineup.so5Leaderboard?.slug || '',
    composeSessionId
  );
  const appearances = missedLineup.so5Leaderboard?.rules?.appearances?.map(
    (rule, i) =>
      missedLineup.appearances.find(appearance => appearance.index === i) ||
      rule
  );

  if (!missedLineup.so5Leaderboard || !appearances?.length) {
    return null;
  }

  const createTeam = () => {
    if (missedLineup.so5Leaderboard?.slug) {
      addItemsToAutoSave(
        appearances
          .filter(appearance => 'id' in appearance)
          .map(({ index, id }) => ({
            position: index,
            benchObjectId: id,
          }))
      );
      navigate(
        getComposeTeamRoute({
          leaderboardSlug: missedLineup.so5Leaderboard.slug,
          sport: Sport.FOOTBALL,
          useComposeFlow20,
          eventType: 'classic',
        })
      );
    }
  };

  return (
    <LineupLayout
      header={
        <Header>
          <LinkOverlay as={ButtonBase} onClick={createTeam}>
            <LineupHeader
              badge={
                <Badge
                  validLineup={false}
                  leaderboard={missedLineup.so5Leaderboard}
                />
              }
              leaderboardName={missedLineup.so5Leaderboard?.displayName || ''}
              lineupCount={missedLineup.so5Leaderboard?.so5LineupsCount}
              label={missedLineup.name}
            />
          </LinkOverlay>

          <CorderBadgeWrapper>
            <CornerBadge color="var(--c-nl-100-opaque)">
              <FontAwesomeIcon icon={faCalendar} />
              <FormattedMessage {...fantasy.lastScores} />
            </CornerBadge>
          </CorderBadgeWrapper>
        </Header>
      }
      footer={
        <LinkOther as="div">
          <Footer>
            <StyledButton onClick={createTeam}>
              <FormattedMessage
                id="ForYouTeams.create"
                defaultMessage="Create this team"
              />
            </StyledButton>
          </Footer>
        </LinkOther>
      }
    >
      <LineupCardsLayout>
        {appearances.map(appearance => {
          if ('id' in appearance) {
            return (
              <PlayerCard
                key={appearance.id}
                appearance={appearance}
                onClick={createTeam}
                showScore
                hidePrediction
              />
            );
          }
          return (
            <LinkOther key={appearance.name} as="button" onClick={createTeam}>
              <Placeholder>
                <SimpleCardPlaceholder
                  icon={<AppearanceRuleIcon appearanceRule={appearance} />}
                  label={appearance.name || ''}
                  loading={false}
                  opaque
                />
              </Placeholder>
            </LinkOther>
          );
        })}
      </LineupCardsLayout>
    </LineupLayout>
  );
};

type Props = {
  slug: string;
  myMissedLineupsCount: number;
};
export const ForYouTeams = ({ slug, myMissedLineupsCount }: Props) => {
  const [fetch, { data }] = useLazyQuery(FOR_YOU_TEAMS_QUERY, {
    variables: {
      slug,
    },
  });

  const { so5Fixture } = data?.so5 || {};

  if (!myMissedLineupsCount) {
    return null;
  }

  return (
    <LeagueTeams
      leagueSlug="for-you"
      leagueColor="var(--c-rivals-win)"
      header={<Img src={header} alt="" width="295" height="44" />}
      onIsVisibleCallback={fetch}
    >
      {so5Fixture
        ? so5Fixture.myMissedLineups?.map(missedLineup => {
            return (
              <StyledLinkBox
                style={
                  {
                    '--bg': `url(${missedLineup.so5Leaderboard?.stadiumUrl})`,
                  } as CSSProperties
                }
                key={missedLineup.slug}
              >
                <MissedLineup missedLineup={missedLineup} />
              </StyledLinkBox>
            );
          })
        : range(myMissedLineupsCount).map(i => (
            <StyledLinkBox key={i}>
              <LineupLayout
                header={<LineupHeader leaderboardName="" label="" />}
                footer={<Footer />}
              >
                <LineupCardsLayout>
                  {range(5).map(appearance => (
                    <SimpleCardPlaceholder
                      key={appearance}
                      icon={null}
                      label=""
                      loading
                      Footer={SlotFooter}
                    />
                  ))}
                </LineupCardsLayout>
              </LineupLayout>
            </StyledLinkBox>
          ))}
    </LeagueTeams>
  );
};
